/**
 * Map a raw error message from the Klever SDK, wallet extension,
 * or smart contract into something a normal user can act on.
 *
 * We surface these strings directly in toasts. Prior state was raw
 * SDK / VM strings like "lowerNonceInTx", "veryHighNonceInTx",
 * "insufficient funds", "signing was cancelled" — accurate but
 * user-hostile.
 *
 * Anything unmatched falls through untouched, so we don't regress
 * error visibility for cases we haven't mapped yet. When a new
 * unmapped error shows up in prod, add a mapping here — that's the
 * whole point of the single helper.
 *
 * Called from:
 *   - `buildSignBroadcast` (utils/kleverTx.ts) — internal at throw sites
 *   - Any caller `catch` block that toasts an error string
 */

interface Pattern {
  match: RegExp;
  message: string;
}

// Ordered — first match wins. Put the more specific patterns first
// (e.g. "insufficient KLV" before "insufficient funds") if we ever
// add distinguishing entries.
const PATTERNS: Pattern[] = [
  // User cancelled the flow at the wallet prompt.
  { match: /user (rejected|denied)/i,             message: 'You cancelled the transaction.' },
  { match: /signing was cancelled/i,              message: 'You cancelled the transaction.' },
  { match: /transaction was not signed/i,         message: 'Signing was interrupted. Please try again in your Klever wallet.' },

  // Wallet not available.
  { match: /klever wallet not found/i,            message: 'Klever wallet not detected. Install the extension or open Klyx in the Klever mobile browser.' },
  { match: /wallet not connected|no.*wallet.*connected/i, message: 'Connect your Klever wallet and try again.' },

  // Funding problems.
  { match: /insufficient (funds|balance|klv)/i,   message: 'Not enough KLV in your wallet to cover this transaction.' },
  { match: /insufficient (payment|amount)/i,      message: 'Payment amount is too low for this action.' },

  // Nonce sync race with the node.
  { match: /(lower|veryHigh)?nonce.*in.*tx|nonce collision/i, message: 'Your wallet was briefly out of sync. Try again in a few seconds.' },

  // Gas limit issues.
  { match: /gas ?limit/i,                          message: 'Transaction ran out of gas. Please try again — if it happens again, contact support.' },

  // Contract access control / state.
  { match: /require.*owner|only owner|not.*authorized|unauthorized/i, message: 'You are not authorized to perform this action.' },
  { match: /paused/i,                              message: 'This action is temporarily paused by an admin.' },
  { match: /banned/i,                              message: 'Your account is restricted from this action. Contact support if you believe this is a mistake.' },
  { match: /token.*(not|isn.?t).*(whitelist|allowed)/i, message: 'This token is not accepted for this action.' },
  { match: /profile.*(not).*(exist|found)/i,       message: 'This profile no longer exists on-chain.' },
  { match: /already (owns|has)/i,                  message: 'You already own this — no change made.' },

  // Trade-side pricing rejects.
  { match: /min(imum)?.*price.*increase|price.*too.*low/i, message: 'Offer must be at least 10% above the current price.' },

  // Generic contract revert (VM error code 57 per smart-contract rules).
  { match: /execution reverted|smart contract error|user error/i, message: 'The transaction was rejected by the smart contract. If this keeps happening, contact support.' },
  // Broader VM error catch-all when nothing else matched.
  { match: /vm error|contract call failed/i,       message: 'The transaction failed on-chain. Please try again — if it happens again, contact support.' },

  // Broadcast-layer failure (network / node down / node throttling).
  { match: /broadcast.*fail|network.*error|fetch.*fail|timeout|too many requests|429/i,
    message: 'Network hiccup talking to Klever. Please try again in a moment.' },
];

/**
 * Best-effort formatting. Extracts a message string from a variety
 * of error shapes (Error, plain object with .message, string, or
 * an object we JSON.stringify), then maps via PATTERNS. Anything
 * unmapped falls through as-is so we never HIDE errors — worst
 * case, the user sees the same string they'd have seen before.
 */
export function formatKleverError(err: unknown, fallback = 'Transaction failed'): string {
  const raw = extractMessage(err) || fallback;
  for (const { match, message } of PATTERNS) {
    if (match.test(raw)) return message;
  }
  return raw;
}

function extractMessage(err: unknown): string {
  if (!err) return '';
  if (typeof err === 'string') return err;
  if (err instanceof Error) return err.message;
  const anyErr = err as Record<string, unknown>;
  const msg = anyErr.message ?? anyErr.error ?? anyErr.msg;
  if (typeof msg === 'string') return msg;
  try {
    return JSON.stringify(err);
  } catch {
    return String(err);
  }
}
